import { Vector3 } from 'three';
import { TrackDef, TrackNode } from '../track/spline';
import { RollKey } from '../track/roll';
import { Section } from '../track/sections';
import { CoasterFile, CURRENT_VERSION, FORMAT, parseCoasterFile } from './schema';
import { TrainConfig } from '../sim/train';
import { SimSettings } from '../sim/physics';
import { DEFAULT_SUPPORT_PARAMS, SupportParams } from '../supports/generate';
import { SupportOverride } from '../supports/types';
import { normalizePrintSettings, PrintSettings } from '../print/types';
import { TrackShapeSettings, normalizeTrackShapeSettings } from '../geometry/gearRack';

/** Fill any missing support knobs so save/export always writes a complete object. */
export function normalizeSupportParams(
  partial: Partial<SupportParams> | null | undefined,
): SupportParams {
  const out = { ...DEFAULT_SUPPORT_PARAMS };
  if (!partial || typeof partial !== 'object') return out;
  for (const key of Object.keys(DEFAULT_SUPPORT_PARAMS) as (keyof SupportParams)[]) {
    const v = partial[key];
    if (v !== undefined && v !== null) {
      (out as Record<string, unknown>)[key] = v;
    }
  }
  return out;
}

/** The single source of truth. Everything else — meshes, LUT, supports — is derived. */
export interface CoasterDocument {
  meta: { name: string; author: string; created: string };
  settings: SimSettings;
  train: TrainConfig;
  start: { position: Vector3; heading: number };
  track: TrackDef;
  /**
   * Derived bank channel rebuilt from `node.forcedBankDeg` on each runtime
   * refresh. Not loaded from / saved to files (legacy free keys are ignored).
   */
  rollKeys: RollKey[];
  /**
   * @deprecated Unused — kept empty for file-format compatibility.
   * Inversions are authored with roll keys (e.g. 180°).
   */
  verticalInverts: number[];
  sections: Section[];
  supports: SupportParams;
  supportsEnabled: boolean;
  supportOverrides: SupportOverride[];
  /** Side-rail diameter, triangular frame, and gear rack. */
  trackShape: TrackShapeSettings;
  print: PrintSettings;
}

export function documentToFile(doc: CoasterDocument): CoasterFile {
  return {
    format: FORMAT,
    version: CURRENT_VERSION,
    meta: { ...doc.meta },
    settings: {
      gravity: doc.settings.gravity,
      friction: doc.settings.friction,
      drag: doc.settings.drag,
      minSpeed: doc.settings.minSpeed,
    },
    train: {
      cars: doc.train.cars,
      carMass: doc.train.carMass,
      seatsPerCar: doc.train.seatsPerCar,
      carLength: doc.train.carLength,
    },
    start: {
      position: [doc.start.position.x, doc.start.position.y, doc.start.position.z],
      heading: doc.start.heading,
    },
    track: {
      closed: doc.track.closed,
      nodes: doc.track.nodes.map((n) => ({
        id: n.id,
        p: [n.p.x, n.p.y, n.p.z],
        hIn: [n.hIn.x, n.hIn.y, n.hIn.z],
        hOut: [n.hOut.x, n.hOut.y, n.hOut.z],
        mode: n.mode,
        ...(n.forcedYawDeg !== undefined ? { forcedYawDeg: n.forcedYawDeg } : {}),
        ...(n.forcedPitchDeg !== undefined ? { forcedPitchDeg: n.forcedPitchDeg } : {}),
        ...(n.forcedPos ? { forcedPos: true } : {}),
        ...(n.forcedBankDeg !== undefined ? { forcedBankDeg: n.forcedBankDeg } : {}),
      })),
      // Banking is authored on nodes (`forcedBankDeg`); do not persist legacy free keys.
      rollKeys: [],
      verticalInverts: [],
      sections: doc.sections.map((s) => ({ ...s })),
    },
    supports: normalizeSupportParams(doc.supports),
    supportsEnabled: doc.supportsEnabled !== false,
    supportOverrides: (doc.supportOverrides ?? []).map((o) => ({ ...o })),
    trackShape: normalizeTrackShapeSettings(doc.trackShape),
    print: normalizePrintSettings(doc.print),
  };
}

export function fileToDocument(data: unknown): CoasterDocument {
  const file = parseCoasterFile(data);
  const nodes: TrackNode[] = file.track.nodes.map((n) => ({
    id: n.id,
    p: new Vector3(...n.p),
    hIn: new Vector3(...n.hIn),
    hOut: new Vector3(...n.hOut),
    mode: n.mode,
    ...(n.forcedYawDeg !== undefined ? { forcedYawDeg: n.forcedYawDeg } : {}),
    ...(n.forcedPitchDeg !== undefined ? { forcedPitchDeg: n.forcedPitchDeg } : {}),
    ...(n.forcedPos ? { forcedPos: true } : {}),
    ...(n.forcedBankDeg !== undefined ? { forcedBankDeg: n.forcedBankDeg } : {}),
  }));
  return {
    meta: { ...file.meta },
    settings: { ...file.settings },
    train: { ...file.train },
    start: { position: new Vector3(...file.start.position), heading: file.start.heading },
    track: { closed: file.track.closed, nodes },
    // Legacy free rollKeys in the file are ignored — banking lives on nodes.
    rollKeys: [],
    verticalInverts: [],
    sections: file.track.sections.map((s) => ({ ...s })),
    supports: normalizeSupportParams(file.supports),
    supportsEnabled: file.supportsEnabled !== false,
    supportOverrides: file.supportOverrides.map((o) => ({ ...o })),
    trackShape: normalizeTrackShapeSettings(file.trackShape),
    print: normalizePrintSettings(file.print),
  };
}

export function cloneDocument(doc: CoasterDocument): CoasterDocument {
  return fileToDocument(documentToFile(doc));
}

export function emptyDocument(name = 'Untitled'): CoasterDocument {
  return {
    meta: { name, author: '', created: new Date().toISOString() },
    settings: { gravity: 9.80665, friction: 0.025, drag: 0.002, minSpeed: 2.0 },
    train: { cars: 5, carMass: 900, seatsPerCar: 4, carLength: 3.2 },
    start: { position: new Vector3(0, 0, 0), heading: 0 },
    track: { closed: false, nodes: [] },
    rollKeys: [],
    verticalInverts: [],
    sections: [],
    supports: normalizeSupportParams(undefined),
    supportsEnabled: true,
    supportOverrides: [],
    trackShape: normalizeTrackShapeSettings(undefined),
    print: normalizePrintSettings(undefined),
  };
}
